import React, { useEffect, useRef } from "react";

const milestones = [
  {
    year: "2024",
    title: "Indigenous Breakout Box Delivery",
    desc: "Delivered custom signal breakout boxes for weapon system integration",
  },
  {
    year: "2023",
    title: "CMS Display Repair Program",
    desc: "Established repair capability for naval combat management displays",
  },
  {
    year: "2022",
    title: "Surveillance Integration",
    desc: "Integrated surveillance electronics for defence platforms",
  },
  {
    year: "2021",
    title: "Processor & SBC Repair",
    desc: "Developed indigenous repair for critical processors and SBCs",
  },
];

export default function ProjectExperience() {
  const sectionRef = useRef(null);
  const progressRef = useRef(null);
  const itemsRef = useRef([]);

  useEffect(() => {
    const section = sectionRef.current;
    const progress = progressRef.current;
    const items = itemsRef.current;

    if (!section || !progress) return;

    const handleScroll = () => {
      const scrollY = window.scrollY;
      const windowH = window.innerHeight;

      const top = section.offsetTop;
      const height = section.offsetHeight;

      const start = top - windowH * 0.6;
      const end = top + height - windowH * 0.6;

      const p = Math.min(Math.max((scrollY - start) / (end - start), 0), 1);

      progress.style.height = `${p * 100}%`;

      const total = items.length || 1;
      items.forEach((el, idx) => {
        if (!el) return;
        const trigger = (idx + 0.25) / total;
        el.classList.toggle("active", p >= trigger);
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section className="pe" ref={sectionRef}>
      <style>{`
        :root{
          --accent:#ff8a00;
          --text:#0f172a;
          --muted:#6b7280;
          --stroke:#eef2f7;
          --radius:14px;
          --shadow:0 16px 34px rgba(15,23,42,.1);
          --shadow-soft:0 10px 22px rgba(15,23,42,.07);
        }

        *{box-sizing:border-box}

        .pe{
          padding:3rem 1.25rem;
          background:#fff;
          font-family:system-ui;
        }

        .pe__wrap{
          max-width:980px;
          margin:0 auto;
          position:relative;
        }

        .pe__head{
          text-align:center;
          margin-bottom:2rem;
        }

        .pe__title{
          font-size:1.4rem;
          font-weight:800;
          margin:0;
        }

        .pe__sub{
          margin:.5rem 0 0;
          color:var(--muted);
          font-size:.9rem;
        }

        /* TIMELINE */
        .timeline{
          position:relative;
        }

        .line{
          position:absolute;
          top:0;
          bottom:0;
          left:50%;
          transform:translateX(-50%);
          width:3px;
          background:#e5e7eb;
          border-radius:999px;
        }

        .progress{
          position:absolute;
          top:0;
          left:0;
          width:100%;
          height:0%;
          background:var(--accent);
          border-radius:999px;
        }

        .node{
          position:relative;
          display:grid;
          grid-template-columns:1fr 1fr;
          align-items:center;
          min-height:140px; /* FIXES DOT DRIFT */
        }

        /* DOT */
        .dotWrap{
          position:absolute;
          left:50%;
          top:50%;
          transform:translate(-50%, -50%);
          pointer-events:none;
        }

        .dot{
          width:10px;
          height:10px;
          border-radius:50%;
          background:var(--accent);
          box-shadow:0 10px 18px rgba(255,138,0,.3);
        }

        /* CARD */
        .card{
          width:min(360px,100%);
          background:#fff;
          border:1px solid var(--stroke);
          border-radius:var(--radius);
          padding:1rem;
          opacity:.25;
          transform:translateY(12px);
          transition:.25s ease;
          box-shadow:var(--shadow-soft);
        }

        .node.active .card{
          opacity:1;
          transform:none;
          box-shadow:var(--shadow);
        }

        .meta{
          display:flex;
          justify-content:space-between;
          margin-bottom:.4rem;
        }

        .year{
          font-size:.7rem;
          font-weight:800;
          color:var(--accent);
        }

        .tag{
          font-size:.7rem;
          color:#94a3b8;
        }

        .card h3{
          margin:0;
          font-size:.95rem;
        }

        .card p{
          margin:.4rem 0 0;
          font-size:.82rem;
          color:var(--muted);
        }

        /* LEFT / RIGHT LOGIC */
        .leftNode .card{
          grid-column:1/2;
          justify-self:end;
          text-align:right;
          margin-right:64px;
        }

        .rightNode .card{
          grid-column:2/3;
          justify-self:start;
          text-align:left;
          margin-left:64px;
        }

        /* MOBILE */
        @media(max-width:860px){
          .node{
            grid-template-columns:1fr;
            padding-left:3rem;
          }

          .line{
            left:22px;
            transform:none;
          }

          .dotWrap{
            left:22px;
          }

          .leftNode .card,
          .rightNode .card{
            grid-column:1/2;
            margin:0;
            text-align:left;
          }
        }
      `}</style>

      <div className="pe__wrap">
        <header className="pe__head">
          <h2 className="pe__title">Project Experience</h2>
          <p className="pe__sub">
            Key milestones in defence electronics delivery
          </p>
        </header>

        <div className="timeline">
          <div className="line">
            <div className="progress" ref={progressRef} />
          </div>

          {milestones.map((m, idx) => {
            const isLeft = idx % 2 === 0;

            return (
              <div
                key={idx}
                ref={(el) => (itemsRef.current[idx] = el)}
                className={`node ${isLeft ? "leftNode" : "rightNode"}`}
              >
                {isLeft ? (
                  <article className="card">
                    <div className="leftMeta">
                      <span className="year">{m.year}</span>
                     
                    </div>
                    <h3>{m.title}</h3>
                    <p>{m.desc}</p>
                  </article>
                ) : (
                  <div />
                )}

                <div className="dotWrap">
                  <span className="dot" />
                </div>

                {!isLeft ? (
                  <article className="card">
                    <div className="leftMeta">
                      <span className="year">{m.year}</span>
                      {/* <span className="tag">Delivery</span> */}
                    </div>
                    <h3>{m.title}</h3>
                    <p>{m.desc}</p>
                  </article>
                ) : (
                  <div />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
